import {CalendarApi} from '@fullcalendar/core';
import moment from 'moment/moment';
import {useCallback, useEffect, useMemo, useState} from 'react';
import {generateRandomUnsafeId} from 'webapp-lib/utils';

import {
  CalendarWorkLogExtendedProps,
  EventApiWithWorkLogProps,
  EventTimers,
  TimerStartData,
} from '../types';

const RECORDING_TICK_INTERVAL_MINUTES = 3;

export const useRecordingEvent = (
  getCalendarApi: () => CalendarApi
): {
  recordingEvent: EventApiWithWorkLogProps | null;
  timer: EventTimers;
} => {
  const [recordingEvent, setRecordingEvent] =
    useState<EventApiWithWorkLogProps | null>(null);

  const updateEventEnd = useCallback((event: EventApiWithWorkLogProps) => {
    event?.setEnd(new Date());
  }, []);

  // update currently recording event
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (recordingEvent) {
      interval = setInterval(
        () => updateEventEnd(recordingEvent),
        RECORDING_TICK_INTERVAL_MINUTES * 1_000 * 60
      );
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [recordingEvent, updateEventEnd]);

  const stopTimer = useCallback(() => {
    if (recordingEvent) {
      recordingEvent.setEnd(new Date());
    }
    setRecordingEvent(null);
  }, [recordingEvent]);

  const startTimer = useCallback(
    (data: TimerStartData) => {
      stopTimer();

      const {title} = data;
      const now = new Date();

      const extendedProps: CalendarWorkLogExtendedProps = {
        activity: data.activity,
        description: data.description,
      };
      const calendarEvent = getCalendarApi().addEvent({
        id: generateRandomUnsafeId(),
        title,
        start: now,
        end: moment(now).add(1, 'seconds').valueOf(),
        extendedProps,
        backgroundColor: data.color,
        editable: true,
      });

      setRecordingEvent(calendarEvent);
    },
    [getCalendarApi, stopTimer]
  );

  const timer: EventTimers = useMemo(
    () => ({
      start: startTimer,
      stop: stopTimer,
    }),
    [startTimer, stopTimer]
  );

  return {
    recordingEvent,
    timer,
  };
};
